import { memo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import './RatingStars.css';

/**
 * RatingStars Component
 *
 * Displays star ratings with support for:
 * - Display mode (show rating)
 * - Interactive mode (select rating)
 * - Half-star support for decimal ratings
 * - Multiple sizes (sm, md, lg)
 * - Keyboard navigation
 */
const RatingStars = memo(function RatingStars({
  rating = 0,
  size = 'md',
  interactive = false,
  onChange,
  showCount = false,
  disabled = false
}) {
  const [hoverRating, setHoverRating] = useState(null);
  const [focusedStar, setFocusedStar] = useState(null);

  // Calculate which stars to show
  const getStarType = (position) => {
    const currentRating = hoverRating !== null ? hoverRating : rating;

    if (currentRating >= position) {
      return 'filled';
    } else if (currentRating >= position - 0.5) {
      return 'half';
    } else {
      return 'empty';
    }
  };

  // Handle star click
  const handleClick = useCallback((starValue) => {
    if (!interactive || disabled) return;
    if (onChange) {
      onChange(starValue);
    }
  }, [interactive, disabled, onChange]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e, starValue) => {
    if (!interactive || disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleClick(starValue);
        break;
      case 'ArrowRight':
        e.preventDefault();
        setFocusedStar(Math.min(5, starValue + 1));
        break;
      case 'ArrowLeft':
        e.preventDefault();
        setFocusedStar(Math.max(1, starValue - 1));
        break;
      default:
        break;
    }
  }, [interactive, disabled, handleClick]);

  // Handle mouse enter
  const handleMouseEnter = useCallback((starValue) => {
    if (!interactive || disabled) return;
    setHoverRating(starValue);
  }, [interactive, disabled]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    if (!interactive || disabled) return;
    setHoverRating(null);
  }, [interactive, disabled]);

  // Render star icon
  const renderStar = (starType) => {
    switch (starType) {
      case 'filled':
        return '★';
      case 'half':
        return '★'; // Will use CSS for half-fill
      case 'empty':
      default:
        return '☆';
    }
  };

  return (
    <div className={`rating-stars rating-stars--${size}`}>
      <div
        className="rating-stars__display"
        role={interactive ? 'radiogroup' : 'img'}
        aria-label={interactive ? 'Rate this product' : `Rated ${rating} out of 5 stars`}
      >
        {[1, 2, 3, 4, 5].map((starValue) => {
          const starType = getStarType(starValue);
          const isFocused = focusedStar === starValue;

          return (
            <span
              key={starValue}
              className={`star star--${starType} ${interactive ? 'star--interactive' : ''} ${isFocused ? 'star--focused' : ''}`}
              onClick={() => handleClick(starValue)}
              onMouseEnter={() => handleMouseEnter(starValue)}
              onMouseLeave={handleMouseLeave}
              onKeyDown={(e) => handleKeyDown(e, starValue)}
              role={interactive ? 'radio' : undefined}
              aria-checked={interactive ? rating === starValue : undefined}
              tabIndex={interactive && !disabled ? 0 : -1}
              aria-disabled={disabled}
            >
              {renderStar(starType)}
            </span>
          );
        })}
      </div>
      {showCount && (
        <span className="rating-stars__count">
          ({rating.toFixed(1)})
        </span>
      )}
    </div>
  );
});

RatingStars.propTypes = {
  rating: PropTypes.number,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  interactive: PropTypes.bool,
  onChange: PropTypes.func,
  showCount: PropTypes.bool,
  disabled: PropTypes.bool
};

export default RatingStars;
